import { useEffect, useState } from "react";
import { toast } from "sonner";
import api, { fmtErr } from "../../lib/api";
import { PageHeader, inputCls, btnPrimary, TierBadge } from "../../components/bits";

const studentTier = (n) => {
  if (!n) return null;
  if (n <= 250) return "small";
  if (n <= 500) return "medium";
  return "large";
};
import { Save, AlertTriangle, Lock } from "lucide-react";

export default function Settings() {
  const [data, setData] = useState(null);
  const [form, setForm] = useState({ stage: "", office: "", contact_email: "", contact_phone: "", total_students: "" });
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.get("/portal/settings").then((r) => {
      setData(r.data);
      const s = r.data.school;
      setForm({
        stage: s.stage || "", office: s.office || "",
        contact_email: s.contact_email || "", contact_phone: s.contact_phone || "",
        total_students: s.total_students || "",
      });
    }).catch((e) => toast.error(fmtErr(e)));
  }, []);

  const save = async () => {
    setBusy(true);
    try {
      await api.put("/portal/settings", form);
      toast.success("تم حفظ إعدادات المدرسة بنجاح");
    } catch (e) {
      toast.error(fmtErr(e));
    } finally {
      setBusy(false);
    }
  };

  if (!data) return <div className="text-center text-mutext py-20 font-head">جارٍ التحميل …</div>;
  const school = data.school;

  return (
    <div data-testid="settings-page" className="max-w-2xl">
      <PageHeader title="إعدادات المدرسة" sub="بيانات الأساس التي تُبنى عليها مؤشرات المنصة" />

      {!school.total_students && (
        <div data-testid="total-students-warning" className="bg-coral-100 text-coral-900 rounded-2xl px-5 py-4 text-sm font-bold mb-5 flex items-center gap-2">
          <AlertTriangle className="w-4 h-4 shrink-0" />
          حقل «إجمالي عدد الطلاب» إلزامي — لا يمكن إضافة فرص تطوعية قبل إدخاله (منعاً للقسمة على صفر).
        </div>
      )}

      <div className="bg-surface rounded-3xl border border-brd shadow-md p-6 space-y-4">
        <div className="flex items-center justify-between gap-3 flex-wrap bg-bg rounded-2xl p-4">
          <div>
            <div data-testid="settings-school-name" className="font-head font-black text-lg">{school.name}</div>
            <div className="text-[11px] text-mutext">المنسق: {data.coordinator.name} — {data.coordinator.email}</div>
          </div>
          <TierBadge tier={studentTier(school.total_students)} />
        </div>

        <div className="grid sm:grid-cols-2 gap-4">
          <div>
            <label className="text-xs font-bold text-mutext block mb-1.5">المرحلة</label>
            <select data-testid="settings-stage-select" value={form.stage} onChange={(e) => setForm({ ...form, stage: e.target.value })} className={inputCls}>
              <option value="">— اختر —</option>
              {["ابتدائية", "متوسطة", "ثانوية", "مجمع تعليمي"].map((s) => <option key={s}>{s}</option>)}
            </select>
          </div>
          <div>
            <label className="text-xs font-bold text-mutext block mb-1.5">مكتب التعليم / المحافظة</label>
            <input data-testid="settings-office-input" value={form.office} onChange={(e) => setForm({ ...form, office: e.target.value })} className={inputCls} />
          </div>
          <div>
            <label className="text-xs font-bold text-mutext block mb-1.5">بريد استقبال التقارير</label>
            <input data-testid="settings-email-input" type="email" value={form.contact_email} onChange={(e) => setForm({ ...form, contact_email: e.target.value })} className={inputCls} dir="ltr" />
          </div>
          <div>
            <label className="text-xs font-bold text-mutext block mb-1.5">رقم التواصل</label>
            <input data-testid="settings-phone-input" value={form.contact_phone} onChange={(e) => setForm({ ...form, contact_phone: e.target.value })} className={inputCls} dir="ltr" />
          </div>
          <div className="sm:col-span-2 grid grid-cols-2 gap-3">
            <div className="rounded-xl bg-bg border border-brd p-3.5">
              <div className="text-[10.5px] text-mutext font-bold mb-1 flex items-center gap-1">
                <Lock className="w-3 h-3" /> إجمالي عدد الطلاب
              </div>
              <div data-testid="settings-total-students-value" className="font-head font-black text-xl text-turq-900 dark:text-turq-300">{school.total_students || "—"}</div>
              <div className="text-[10px] text-mutext mt-1">أساس مؤشر التمكين — يُدار من الإدارة العامة فقط</div>
            </div>
            <div className="rounded-xl bg-bg border border-brd p-3.5">
              <div className="text-[10.5px] text-mutext font-bold mb-1 flex items-center gap-1">
                <Lock className="w-3 h-3" /> عدد الموظفين
              </div>
              <div data-testid="settings-staff-count-value" className="font-head font-black text-xl text-turq-900 dark:text-turq-300">{school.staff_count ?? "—"}</div>
              <div className="text-[10px] text-mutext mt-1">يُدار من الإدارة العامة فقط</div>
            </div>
          </div>
        </div>

        <button data-testid="settings-save-btn" onClick={save} disabled={busy} className={`${btnPrimary} w-full justify-center`}>
          <Save className="w-4 h-4" /> {busy ? "جارٍ الحفظ …" : "حفظ الإعدادات"}
        </button>
      </div>
    </div>
  );
}
